/**
 * BRAND CLAIMS ADMIN
 * 
 * Admin page to review and approve/deny brand ownership claims.
 * 
 * Features:
 * - View all pending claims
 * - See verification details (method, code, social handle, etc.)
 * - One-click approve/deny
 * - View claim history
 * - Quick links to brand pages
 * 
 * REQUIRES: isAdmin: true on your user document in Firestore
 * 
 * SETUP:
 * 
 * Option A: Put in pages/admin/ folder (recommended)
 *   - File location: src/pages/admin/BrandClaimsAdmin.tsx
 *   - Import in App.tsx: import BrandClaimsAdmin from './pages/admin/BrandClaimsAdmin';
 *   - Keep imports below as-is (../../firebase/config)
 * 
 * Option B: Put directly in pages/ folder
 *   - File location: src/pages/BrandClaimsAdmin.tsx
 *   - Import in App.tsx: import BrandClaimsAdmin from './pages/BrandClaimsAdmin';
 *   - Change import below to: '../firebase/config' and '../types/brand'
 * 
 * Route to add in App.tsx:
 *   <Route path="/admin/brand-claims" element={<BrandClaimsAdmin />} />
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from '../../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import {
  Shield,
  ArrowLeft,
  RefreshCw,
  Check,
  X,
  ExternalLink,
  Mail,
  Globe,
  Instagram,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  User,
  Building2,
  ChevronDown,
  ChevronRight,
  Copy,
  Eye,
} from 'lucide-react';
import type { Brand, VerificationMethod } from '../../types/brand';

// Helper to format timestamp
function formatDate(date: Date | Timestamp | undefined): string {
  if (!date) return 'Unknown';
  const d = date instanceof Timestamp ? date.toDate() : new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Helper to get method display info
function getMethodInfo(method: VerificationMethod): { icon: any; label: string; color: string } {
  switch (method) {
    case 'domain-email':
      return { icon: Mail, label: 'Domain Email', color: 'text-blue-600 bg-blue-100' };
    case 'website-code':
      return { icon: Globe, label: 'Website Code', color: 'text-green-600 bg-green-100' };
    case 'social-media':
      return { icon: Instagram, label: 'Social Media', color: 'text-pink-600 bg-pink-100' };
    case 'manual-review':
      return { icon: FileText, label: 'Manual Review', color: 'text-amber-600 bg-amber-100' };
    default:
      return { icon: FileText, label: 'Unknown', color: 'text-gray-600 bg-gray-100' };
  }
}

export default function BrandClaimsAdmin() {
  // Auth state
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  // Data state
  const [pendingClaims, setPendingClaims] = useState<Brand[]>([]);
  const [recentlyProcessed, setRecentlyProcessed] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState<string | null>(null);

  // UI state
  const [expandedClaim, setExpandedClaim] = useState<string | null>(null);
  const [showProcessed, setShowProcessed] = useState(false);
  const [denyReason, setDenyReason] = useState<string>('');
  const [showDenyModal, setShowDenyModal] = useState<string | null>(null);

  // Check admin status
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user?.uid || null);

      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setIsAdmin(userDoc.data()?.isAdmin === true);
        }
      } else {
        setIsAdmin(false);
      }
      setCheckingAdmin(false);
    });
    return () => unsubscribe();
  }, []);

  // Load claims
  const loadClaims = async () => {
    setLoading(true);
    try {
      // Get pending claims (status = 'claimed')
      const pendingQuery = query(
        collection(db, 'brands'),
        where('status', '==', 'claimed'),
        orderBy('claimRequest.submittedAt', 'desc')
      );
      
      const pendingSnapshot = await getDocs(pendingQuery);
      const pending = pendingSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Brand[];
      
      setPendingClaims(pending);

      // Get recently processed (verified or rejected in last 30 days)
      const recentQuery = query(
        collection(db, 'brands'),
        where('status', 'in', ['verified', 'rejected']),
        orderBy('verifiedAt', 'desc')
      );
      
      const recentSnapshot = await getDocs(recentQuery);
      const recent = recentSnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
        }) as Brand)
        .filter(b => {
          // Only show last 30 days
          if (!b.verifiedAt) return false;
          const verifiedDate = b.verifiedAt instanceof Timestamp
            ? b.verifiedAt.toDate()
            : new Date(b.verifiedAt);
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          return verifiedDate > thirtyDaysAgo;
        });
      
      setRecentlyProcessed(recent);
    } catch (error) {
      console.error('Error loading claims:', error);
    }
    setLoading(false);
  };

  // Load on mount
  useEffect(() => {
    if (isAdmin) {
      loadClaims();
    }
  }, [isAdmin]);

  // Approve claim
  const handleApprove = async (brand: Brand) => {
    if (!brand.claimRequest || !currentUser) return;
    
    setProcessing(brand.id);
    try {
      // Update brand document
      await updateDoc(doc(db, 'brands', brand.id), {
        status: 'verified',
        ownerId: brand.claimRequest.userId,
        ownerEmail: brand.claimRequest.userEmail,
        verifiedAt: serverTimestamp(),
        verifiedBy: currentUser,
        // Keep claimRequest for history, but could clear it
      });

      // Update user document with brand ownership
      await updateDoc(doc(db, 'users', brand.claimRequest.userId), {
        role: 'brand',
        ownsBrandId: brand.id,
        ownsBrandName: brand.name,
      });

      // Refresh list
      await loadClaims();
      setExpandedClaim(null);
    } catch (error) {
      console.error('Error approving claim:', error);
      alert('Failed to approve claim. Check console for details.');
    }
    setProcessing(null);
  };

  // Deny claim
  const handleDeny = async (brand: Brand) => {
    if (!brand.claimRequest || !currentUser) return;
    
    setProcessing(brand.id);
    try {
      // Update brand document
      await updateDoc(doc(db, 'brands', brand.id), {
        status: 'unclaimed', // Back to unclaimed so someone else can try
        verifiedAt: serverTimestamp(),
        verifiedBy: currentUser,
        lastDenialReason: denyReason || 'No reason provided',
        // Clear the claim request
        claimRequest: null,
      });

      // Refresh list
      await loadClaims();
      setShowDenyModal(null);
      setDenyReason('');
      setExpandedClaim(null);
    } catch (error) {
      console.error('Error denying claim:', error);
      alert('Failed to deny claim. Check console for details.');
    }
    setProcessing(null);
  };

  // Copy to clipboard
  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
  };

  // Render claim card
  const renderClaimCard = (brand: Brand, isPending: boolean) => {
    const claim = brand.claimRequest;
    const isExpanded = expandedClaim === brand.id;
    const methodInfo = claim ? getMethodInfo(claim.method) : null;
    const MethodIcon = methodInfo?.icon || FileText;

    return (
      <div
        key={brand.id}
        className={`border rounded-xl overflow-hidden transition-all ${
          isPending
            ? 'border-amber-500/50 bg-amber-500/10'
            : brand.status === 'verified'
            ? 'border-green-500/50 bg-green-500/10'
            : 'border-red-500/50 bg-red-500/10'
        }`}
      >
        {/* Header - Always visible */}
        <div
          className="p-4 cursor-pointer hover:bg-slate-700/30 transition-colors"
          onClick={() => setExpandedClaim(isExpanded ? null : brand.id)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isExpanded ? (
                <ChevronDown className="w-5 h-5 text-glass-muted" />
              ) : (
                <ChevronRight className="w-5 h-5 text-glass-muted" />
              )}

              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-glass">{brand.name}</h3>
                  {isPending && (
                    <span className="px-2 py-0.5 bg-amber-500/30 text-amber-300 text-xs font-medium rounded-full">
                      Pending
                    </span>
                  )}
                  {brand.status === 'verified' && (
                    <span className="px-2 py-0.5 bg-green-500/30 text-green-300 text-xs font-medium rounded-full flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Verified
                    </span>
                  )}
                  {brand.status === 'rejected' && (
                    <span className="px-2 py-0.5 bg-red-500/30 text-red-300 text-xs font-medium rounded-full flex items-center gap-1">
                      <XCircle className="w-3 h-3" />
                      Denied
                    </span>
                  )}
                </div>

                {claim && (
                  <div className="flex items-center gap-3 mt-1 text-sm text-glass-secondary">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${methodInfo?.color}`}>
                      <MethodIcon className="w-3 h-3" />
                      {methodInfo?.label}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(claim.submittedAt)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Quick actions */}
            {isPending && (
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => handleApprove(brand)}
                  disabled={processing === brand.id}
                  className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                >
                  {processing === brand.id ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  Approve
                </button>
                <button
                  onClick={() => setShowDenyModal(brand.id)}
                  disabled={processing === brand.id}
                  className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                >
                  <X className="w-4 h-4" />
                  Deny
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Expanded details */}
        {isExpanded && claim && (
          <div className="px-4 pb-4 border-t border-slate-600 bg-slate-800/30">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {/* Claimant Info */}
              <div className="space-y-3">
                <h4 className="font-semibold text-glass flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Claimant Info
                </h4>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between p-2 bg-slate-700/40 rounded-lg">
                    <span className="text-glass-secondary">Name:</span>
                    <span className="font-medium text-glass">{claim.userName}</span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-slate-700/40 rounded-lg">
                    <span className="text-glass-secondary">Email:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-glass">{claim.userEmail}</span>
                      <button
                        onClick={() => copyToClipboard(claim.userEmail)}
                        className="p-1 hover:bg-slate-600 rounded"
                        title="Copy email"
                      >
                        <Copy className="w-3 h-3 text-glass-muted" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-slate-700/40 rounded-lg">
                    <span className="text-glass-secondary">User ID:</span>
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-slate-700 px-1 py-0.5 rounded text-glass-secondary">{claim.userId.slice(0, 12)}...</code>
                      <button
                        onClick={() => copyToClipboard(claim.userId)}
                        className="p-1 hover:bg-slate-600 rounded"
                        title="Copy user ID"
                      >
                        <Copy className="w-3 h-3 text-glass-muted" />
                      </button>
                    </div>
                  </div>
                  {claim.role && (
                    <div className="flex items-center justify-between p-2 bg-slate-700/40 rounded-lg">
                      <span className="text-glass-secondary">Role:</span>
                      <span className="font-medium text-glass">{claim.role}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Verification Details */}
              <div className="space-y-3">
                <h4 className="font-semibold text-glass flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Verification Details
                </h4>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between p-2 bg-slate-700/40 rounded-lg">
                    <span className="text-glass-secondary">Method:</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${methodInfo?.color}`}>
                      <MethodIcon className="w-3 h-3" />
                      {methodInfo?.label}
                    </span>
                  </div>

                  {claim.verificationCode && (
                    <div className="flex items-center justify-between p-2 bg-slate-700/40 rounded-lg">
                      <span className="text-glass-secondary">Code:</span>
                      <div className="flex items-center gap-2">
                        <code className="font-mono text-purple-400 font-medium">{claim.verificationCode}</code>
                        <button
                          onClick={() => copyToClipboard(claim.verificationCode!)}
                          className="p-1 hover:bg-slate-600 rounded"
                          title="Copy code"
                        >
                          <Copy className="w-3 h-3 text-glass-muted" />
                        </button>
                      </div>
                    </div>
                  )}

                  {claim.domainEmail && (
                    <div className="flex items-center justify-between p-2 bg-slate-700/40 rounded-lg">
                      <span className="text-glass-secondary">Domain Email:</span>
                      <span className="font-medium text-glass">{claim.domainEmail}</span>
                    </div>
                  )}

                  {claim.socialHandle && (
                    <div className="flex items-center justify-between p-2 bg-slate-700/40 rounded-lg">
                      <span className="text-glass-secondary">Instagram:</span>
                      <a
                        href={`https://instagram.com/${claim.socialHandle}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-pink-400 hover:underline flex items-center gap-1"
                      >
                        @{claim.socialHandle}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Notes */}
            {claim.notes && (
              <div className="mt-4 p-3 bg-blue-500/20 border border-blue-500/50 rounded-lg">
                <h4 className="font-medium text-blue-300 mb-1">Additional Notes:</h4>
                <p className="text-blue-200 text-sm whitespace-pre-wrap">{claim.notes}</p>
              </div>
            )}

            {/* Brand links */}
            <div className="mt-4 flex items-center gap-3">
              <Link
                to={`/brands/${brand.slug}`}
                className="text-sm text-purple-400 hover:underline flex items-center gap-1"
              >
                <Eye className="w-4 h-4" />
                View Brand Page
              </Link>
              {brand.website && (
                <a
                  href={brand.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-glass-secondary hover:underline flex items-center gap-1"
                >
                  <Globe className="w-4 h-4" />
                  Website
                </a>
              )}
              {brand.instagram && (
                <a
                  href={`https://instagram.com/${brand.instagram}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-pink-400 hover:underline flex items-center gap-1"
                >
                  <Instagram className="w-4 h-4" />
                  Instagram
                </a>
              )}
            </div>
          </div>
        )}

        {/* Deny Modal */}
        {showDenyModal === brand.id && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="glass-modal max-w-md w-full p-6">
              <h3 className="text-lg font-bold text-glass mb-2 font-heading">Deny Claim</h3>
              <p className="text-glass-secondary mb-4 font-body">
                Are you sure you want to deny the claim for <strong className="text-glass">{brand.name}</strong>?
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-glass-secondary mb-1 font-body">
                  Reason (optional)
                </label>
                <textarea
                  value={denyReason}
                  onChange={(e) => setDenyReason(e.target.value)}
                  placeholder="e.g., Could not verify ownership..."
                  rows={3}
                  className="textarea-glass"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDenyModal(null);
                    setDenyReason('');
                  }}
                  className="flex-1 px-4 py-2 border border-slate-600 text-glass-secondary rounded-lg hover:bg-slate-700/50 font-body"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeny(brand)}
                  disabled={processing === brand.id}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2 font-body"
                >
                  {processing === brand.id ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <X className="w-4 h-4" />
                  )}
                  Deny Claim
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Loading state
  if (checkingAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="glass-card p-6 text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-purple-400" />
          <p className="text-glass-secondary">Checking admin status...</p>
        </div>
      </div>
    );
  }

  // Not admin
  if (!currentUser || !isAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="glass-card p-6 text-center">
          <Shield className="w-16 h-16 mx-auto mb-4 text-red-400" />
          <h1 className="text-2xl font-bold text-glass mb-2 font-heading">Admin Access Required</h1>
          <p className="text-glass-secondary font-body">You must be logged in as an admin to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8">
      {/* Back Link */}
      <Link
        to="/admin"
        className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 mb-4 font-body"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Admin Dashboard
      </Link>

      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl shadow-lg p-6 mb-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 className="w-10 h-10" />
            <div>
              <h1 className="text-2xl font-bold font-heading">Brand Claims</h1>
              <p className="opacity-90 font-body">Review and verify brand ownership requests</p>
            </div>
          </div>

          <button
            onClick={loadClaims}
            disabled={loading}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg flex items-center gap-2 transition-colors font-body"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-amber-500/20 border border-amber-500/50 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-amber-400">{pendingClaims.length}</div>
          <div className="text-sm text-amber-300">Pending</div>
        </div>
        <div className="bg-green-500/20 border border-green-500/50 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-green-400">
            {recentlyProcessed.filter(b => b.status === 'verified').length}
          </div>
          <div className="text-sm text-green-300">Approved (30d)</div>
        </div>
        <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-red-400">
            {recentlyProcessed.filter(b => b.status === 'rejected').length}
          </div>
          <div className="text-sm text-red-300">Denied (30d)</div>
        </div>
        <div className="bg-purple-500/20 border border-purple-500/50 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-purple-400">
            {pendingClaims.length + recentlyProcessed.length}
          </div>
          <div className="text-sm text-purple-300">Total Activity</div>
        </div>
      </div>

      {/* Pending Claims */}
      <div className="glass-card p-6 mb-6">
        <h2 className="text-xl font-bold text-glass flex items-center gap-2 mb-4 font-body">
          <AlertCircle className="w-6 h-6 text-amber-400" />
          Pending Claims
          {pendingClaims.length > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-amber-500/20 text-amber-300 text-sm rounded-full">
              {pendingClaims.length}
            </span>
          )}
        </h2>

        {loading ? (
          <div className="text-center py-8">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-purple-400" />
            <p className="text-glass-secondary">Loading claims...</p>
          </div>
        ) : pendingClaims.length === 0 ? (
          <div className="text-center py-8 text-glass-secondary">
            <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No pending claims! 🎉</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pendingClaims.map(brand => renderClaimCard(brand, true))}
          </div>
        )}
      </div>

      {/* Recently Processed */}
      {recentlyProcessed.length > 0 && (
        <div className="glass-card p-6">
          <button
            onClick={() => setShowProcessed(!showProcessed)}
            className="w-full flex items-center justify-between text-left"
          >
            <h2 className="text-xl font-bold text-glass flex items-center gap-2 font-body">
              <Clock className="w-6 h-6 text-glass-muted" />
              Recently Processed
              <span className="ml-2 px-2 py-0.5 bg-slate-700 text-glass-secondary text-sm rounded-full">
                {recentlyProcessed.length}
              </span>
            </h2>
            {showProcessed ? (
              <ChevronDown className="w-5 h-5 text-glass-muted" />
            ) : (
              <ChevronRight className="w-5 h-5 text-glass-muted" />
            )}
          </button>

          {showProcessed && (
            <div className="mt-4 space-y-3">
              {recentlyProcessed.map(brand => renderClaimCard(brand, false))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
